import { Head, Link, usePage } from "@inertiajs/react"
import { ArrowLeft } from "lucide-react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import AuthLayout from "@/layouts/auth-layout"
import { newUserSessionPath, resendActivationLinkPath } from "@/routes"

function csrfToken() {
  return (
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content") ?? ""
  )
}

export default function InvitationError() {
  // Landing-page buyers (IMP-219) get a resend_token so they can request a
  // fresh activation link themselves; regular invites get null.
  const { resend_token, partner } = usePage().props as unknown as {
    resend_token: string | null
    partner: { name: string } | null
  }
  const workspaceName = partner?.name ?? "Imperative"

  const [state, setState] = useState<"idle" | "sending" | "sent">("idle")

  async function requestNewLink() {
    if (!resend_token) return
    setState("sending")
    try {
      await fetch(resendActivationLinkPath(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        credentials: "same-origin",
        body: JSON.stringify({ token: resend_token }),
      })
    } finally {
      // Always land on "sent" — the endpoint deliberately doesn't reveal
      // whether the token mapped to a real account.
      setState("sent")
    }
  }

  return (
    <AuthLayout title="" description="">
      <Head title="Invitation link is no longer valid" />
      <div className="flex w-full flex-col gap-6">
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-xl">
              {resend_token
                ? "Your invitation link expired"
                : "Invite link is no longer valid"}
            </CardTitle>
            <CardDescription>
              {resend_token
                ? state === "sent"
                  ? "Done — if that link belonged to an account, a fresh one is on its way. Check your inbox."
                  : "Invitation links expire for security. Request a new one and we’ll email it to you right away."
                : `The invite link to your ${workspaceName} workspace has expired or was withdrawn. Please ask the person who invited you to send a new one.`}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            {resend_token && state !== "sent" ? (
              <Button
                onClick={() => void requestNewLink()}
                disabled={state === "sending"}
                loading={state === "sending"}
              >
                {state === "sending" ? "Sending…" : "Email me a new link"}
              </Button>
            ) : (
              <Button
                variant="outline"
                asChild
                className="flex items-center gap-2"
              >
                <Link href={newUserSessionPath()}>
                  <ArrowLeft className="h-4 w-4" />
                  Back to login
                </Link>
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </AuthLayout>
  )
}
